import { DataTable, FilterBar, PageHeader, type Column, type FilterConfig } from '@zyxantech/tux'
import { LineChart, Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetGatewaysQuery } from '@/api/gateways.api'
import { useGetTagsQuery, type Tag } from '@/api/tags.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { TagValueCell } from '@/components/tags/TagValueCell'
import { TagWriteDialog } from '@/components/tags/TagWriteDialog'
import { PAGINATION } from '@/constants/pagination'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { usePermission } from '@/hooks/usePermission'
import { buildPath } from '@/utils/url'

interface TagFilters {
  gatewayId: string
  search: string
}

export function TagsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const canWrite = usePermission(RESOURCE.TAG, ACTION.UPDATE)

  const [filters, setFilters] = useState<TagFilters>({
    gatewayId: searchParams.get('gatewayId') ?? '',
    search: '',
  })
  const [writeTag, setWriteTag] = useState<Tag | null>(null)

  const { data: gatewaysData } = useGetGatewaysQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  const { data, isLoading, error, refetch } = useGetTagsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
    ...(filters.gatewayId ? { gatewayId: filters.gatewayId } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  })

  const filterConfig = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'gatewayId',
        type: 'select',
        label: 'Gateway',
        placeholder: 'All gateways',
        options: (gatewaysData?.items ?? []).map((g) => ({ label: g.name, value: g.id })),
      },
      { key: 'search', type: 'text', label: 'Search', placeholder: 'Search tags…' },
    ],
    [gatewaysData],
  )

  const columns: Column<Tag>[] = [
    { key: 'name', header: 'Name', accessor: (t) => t.name, sortable: true },
    {
      key: 'address',
      header: 'Address',
      accessor: (t) => <span className="font-mono text-xs">{t.address}</span>,
    },
    { key: 'dataType', header: 'Type', accessor: (t) => t.dataType },
    {
      key: 'value',
      header: 'Value',
      accessor: (t) => <TagValueCell tagId={t.id} unit={t.unit} dataType={t.dataType} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      accessor: (t) => (
        <span className="flex items-center justify-end gap-3">
          <button
            type="button"
            title="History"
            onClick={() => navigate(buildPath(ROUTES.TAG_HISTORY, { id: t.id }))}
            className="text-text-muted hover:text-accent"
          >
            <LineChart size={15} />
          </button>
          {canWrite && (
            <button
              type="button"
              title="Write value"
              onClick={() => setWriteTag(t)}
              className="text-text-muted hover:text-accent"
            >
              <Pencil size={14} />
            </button>
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Tags" subtitle="Live tag values across gateways" />

      <div className="mb-4">
        <FilterBar
          filters={filterConfig}
          values={filters}
          onChange={(key, value) =>
            setFilters((f) => ({ ...f, [key]: typeof value === 'string' ? value : '' }))
          }
          onClear={() => setFilters({ gatewayId: '', search: '' })}
        />
      </div>

      {error ? (
        <ErrorState error={error} retry={() => void refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          loading={isLoading}
          rowKey={(t) => t.id}
          emptyMessage="No tags match the current filters."
        />
      )}

      <TagWriteDialog tag={writeTag} open={writeTag !== null} onClose={() => setWriteTag(null)} />
    </div>
  )
}
